import React, { FC, useState, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import FormStep from '../step/FormStep';
import { Button, Card, Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
//import { Stripe, StripeElements } from '@stripe/stripe-js';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useSportspay, useElements as useSportspayElements, loadSportspayScript } from './kreezee_V1.0';
import { Formik, FormikValues, FormikTouched } from 'formik';
import { buildSchema, buildValues } from '../utilities/FormikBuilder';
import { StepModel } from '../../../../models/form/step';
import RulesHandler, { addSection, removeSection, RulesHandlerLogic } from '../utilities/RulesHandler';
import { formatValues, initializeSteps } from '../utilities/FormUtilities';
import { BuildedSchema, BuildedValues, DataFormModel } from '../utilities/FormModels';
import { FormattedMessage } from 'react-intl';
import { Discount, FormModel, FormStates } from '../../../../models/form/form';
import { postSubmission, deleteSubmission } from '../../../../services/form/FormService';
import Feedback from '../../../library/inputs/feedback/Feedback';
import { getLanguage } from '../../../../utilities/LocalStorage';
import { addErrorToast } from '../../../errors/toast/ToastUtils';
import FormSummary from '../summary/FormSummary';
import PaymentSection from '../payment/PaymentSection';
import { calculateSummary } from '../summary/useFormSummary';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { useIntl } from 'react-intl';
import { OrganizationModel } from '../../../../models/organization/organization';
import Avatar from '../../../layout/avatar/Avatar';
import ScrollToError from '../utilities/ScrollToError';
import ModalComponent from '../../../modal/ModalComponent';
import { noop } from '../../../../utilities/FunctionUtilities';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../../../models/security/security';
import LongTextField from '../controls/longtext/LongTextField';
import classnames from 'classnames';
import _ from 'lodash';
import { getText } from '../../../../utilities/CultureUtilities';

export interface Props {
  form: FormModel;
  organization: OrganizationModel;
  showLimitReached?: boolean;
  showShareModal?: boolean;
  readOnlyMode?: boolean;
  data?: DataFormModel;
  formikRef?: any;
  setupIntentId?: string;
  clientSecret: string;
}

export interface StepDefinition {
  type: 'form-step' | 'summary-step';
  id: number | string;
}

enum PAYMENT_METHODS {
  CREDIT_CARD = 'credit_card',
  OTHER = 'other',
}

const scrollToTop = () => {
  const topbarHeight = document.querySelector('header.topbar')?.clientHeight || 0;
  const progressElement = document.querySelector('.form-progress')?.getBoundingClientRect();

  if (progressElement && progressElement.y < progressElement.height) {
    window.scrollBy({
      top: progressElement.y - topbarHeight - 30,
    });
  }
};

const FormSteps: FC<Props> = ({
  form,
  organization,
  readOnlyMode,
  data,
  showLimitReached,
  setupIntentId,
  showShareModal,
  clientSecret,
}: Props) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [recaptchaRequired, setRecaptchaRequired] = useState<boolean>(false);
  const [noFields, setNoFields] = useState<boolean>(false);
  const [limitReached, setLimitReached] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [touchedSteps, setTouchedSteps] = useState<Record<string, FormikTouched<FormikValues>>>({});
  const [stepsState, setStepsState] = useState<StepModel[] | undefined>(undefined);
  const [formikSchema, setFormikSchema] = useState<BuildedSchema | undefined>(undefined);
  const [formikValues, setFormikValues] = useState<BuildedValues | undefined>(undefined);
  const [steps, setSteps] = useState<StepDefinition[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [showUrlLink, setShowUrlLink] = useState(true);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalTextValue, setModalTextValue] = useState('');
  const [formSecurity, setFormSecurity] = useState<SecurityModel | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);
  const [cardHolderName, setCardHolderName] = useState<string | null>(null);
  const [otherPayment, setOtherPayment] = useState<boolean | null>(null);
  const [paymentMode, setPaymentMode] = useState(
    _.find(form.paymentModes?.modes, { mode: 'single' })?.id || form.paymentModes?.modes[0].id,
  );
  const [discount, setDiscount] = useState<any>(null);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const history = useHistory();
  const intl = useIntl();
  const website = window.location.origin;

  if (!organization || !form) return null;

  const organizationId = organization.id;

  let stripe: any = null;
  let elements: any = null;

  if (form.paymentEnabled && form.paymentAccount) {
    if (form.paymentProvider == 'sportspay') {
      stripe = useSportspay();
      elements = useSportspayElements();
    } else {
      stripe = useStripe();
      elements = useElements();
    }
  }
  const renderUrlLink = () => {
    return `${website}/registrations/embed/${form.id}`;
  };

  const renderHtmlCode = () => {
    return `<iframe id="${form.id}" scrolling="auto" frameborder="0" src="${website}/registrations/embed/${form.id}" width="100%">Loading</iframe> <script> var eventMethod = (window.addEventListener ? 'addEventListener' : 'attachEvent'); var eventer = window[eventMethod]; var messageEvent = (eventMethod == 'attachEvent' ? 'onmessage' : 'message'); eventer(messageEvent, function(e) { if (e.data.id == "${form.id}") {   document.getElementById("${form.id}").height = e.data.height; } }); </script>`;
  };

  useEffect(() => {
    const loadScript = async () => {
      if (clientSecret && form.paymentProvider == 'sportspay') {
        const script = await loadSportspayScript(clientSecret);
        script.onerror = () => {
          addErrorToast({ message: 'Error loading SportsPay script' });
          setDisableForm(true);
        };
      }
    };
    loadScript();
  }, [clientSecret]);

  useEffect(() => {
    if (security) {
      setFormSecurity(getSecuritySection(security, SecuritySections.FORM));
    }
  }, [security]);

  useEffect(() => {
    //Check if form has fields in it
    let haveFields = false;
    if (form.content.steps) {
      for (const step of form.content.steps) {
        for (const section of step.sections) {
          if (section.fields.length > 0) {
            haveFields = true;
          }
        }
      }
    }

    const _steps: StepDefinition[] = [];

    form.content.steps.forEach(function (step) {
      _steps.push({ id: step.id, type: 'form-step' });
    });

    _steps.push({ id: 'summary', type: 'summary-step' });

    setSteps(_steps);

    if (!haveFields) {
      setNoFields(true);
      return;
    }

    if (showLimitReached && form.submissionLimit !== null && form.submissionCount >= form.submissionLimit) {
      setLimitReached(true);
      return;
    } else {
      setLimitReached(false);
    }
    //Initialization
    const initializedSteps = initializeSteps(form.content.steps, data?.fields);
    const formikSchema = buildSchema(initializedSteps, form.fieldProperties);
    const formikValues = buildValues(initializedSteps, data?.fields, intl.locale);
    setFormikSchema(formikSchema);
    setFormikValues(formikValues);
    setStepsState(initializedSteps);
    RulesHandlerLogic(formikValues, initializedSteps, form.content.rules, (steps) => setStepsState(steps));
    setModalTextValue(renderUrlLink());
  }, []);

  const onSubmit = async (values) => {
    if (submitting) return;

    setSubmitting(true);
    if (stepsState) {
      const { total } = calculateSummary(values, form, intl, discount);
      if (customerEmail && (cardHolderName || !form.paymentEnabled || otherPayment || total == 0)) {
        const submissionValues = formatValues(stepsState, values);
        const formData = new FormData();
        let setupResult;

        formData.append('Content', JSON.stringify(submissionValues.fields));
        formData.append('CustomerEmail', customerEmail);
        formData.append('Culture', intl.locale);

        for (const file of submissionValues.files) {
          formData.append(file.id, file.value);
        }

        if (form.recaptchaEnabled) {
          formData.append('RecaptchaToken', recaptchaToken);
        }

        if (discount && discount.code) {
          formData.append('Coupon', discount.code);
        }

        if (setupIntentId) {
          formData.append('SetupIntentId', setupIntentId);
        }
        if (form.paymentEnabled) {
          otherPayment
            ? formData.append('PaymentMethod', PAYMENT_METHODS.OTHER)
            : formData.append('PaymentMethod', PAYMENT_METHODS.CREDIT_CARD);
        }

        if (paymentMode) {
          formData.append('PaymentMode', paymentMode);
        }

        if (stripe && form.paymentEnabled && !otherPayment && form.paymentProvider == 'sportspay') {
          const cardNumber = document.getElementById('cardNumber') as HTMLInputElement | null;
          const expiryDate = document.getElementById('expiryDate') as HTMLInputElement | null;
          const cvc = document.getElementById('cvc') as HTMLInputElement | null;
          if (cardNumber && expiryDate && cvc && cardHolderName) {
            const cardData = {
              cardHolderName: cardHolderName,
              cardNumber: cardNumber.value,
              expiryDate: expiryDate.value,
              cvc: cvc.value,
              host: clientSecret,
              clientSecret: setupIntentId,
            };
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            setupResult = await stripe.confirmSetup({ elements, confirmParams: cardData });
            if (!setupResult.error) {
              formData.append('token', setupResult.setupIntent.id);
              formData.append('cardHolderName', cardHolderName);
            }
          }
        }
        if (setupResult && setupResult?.error) {
          addErrorToast({ message: setupResult.error.message });
        } else {
          await postSubmission(organizationId, form.id, formData)
            .then(async (response) => {
              const { data: submissionId } = response;
              if (form.paymentEnabled && stripe && elements && !otherPayment) {
                if (total > 0) {
                  const baseUrl = window.location.origin;

                  if (form.paymentProvider == 'stripe') {
                    setupResult = await stripe.confirmSetup({
                      //`Elements` instance that was used to create the Payment Element
                      elements,
                      confirmParams: {
                        return_url: `${baseUrl}/registrations/${form.id}/confirm/${submissionId}`,
                        payment_method_data: {
                          billing_details: {
                            email: customerEmail,
                            name: cardHolderName,
                          },
                        },
                      },
                    });
                  }

                  if (setupResult.error) {
                    switch (setupResult.error.code) {
                      case 'card_declined':
                        switch (setupResult.error.decline_code) {
                          case 'insufficient_funds':
                            addErrorToast({
                              message: intl.formatMessage({ id: 'formPublic.postSubmission.insufficientFunds' }),
                            });
                            break;
                          case 'lost_card':
                            addErrorToast({
                              message: intl.formatMessage({ id: 'formPublic.postSubmission.lostCard' }),
                            });
                            break;
                          case 'stolen_card':
                            addErrorToast({
                              message: intl.formatMessage({ id: 'formPublic.postSubmission.stolenCard' }),
                            });
                            break;
                          default:
                            addErrorToast({
                              message: intl.formatMessage({ id: 'formPublic.postSubmission.genericDecline' }),
                            });
                            break;
                        }
                        break;
                      case 'expired_card':
                        addErrorToast({
                          message: intl.formatMessage({ id: 'formPublic.postSubmission.expiredCard' }),
                        });
                        break;
                      case 'incorrect_cvc':
                        addErrorToast({
                          message: intl.formatMessage({ id: 'formPublic.postSubmission.incorrectCVC' }),
                        });
                        break;
                      case 'processing_error':
                        addErrorToast({
                          message: intl.formatMessage({ id: 'formPublic.postSubmission.processingError' }),
                        });
                        break;
                      case 'incorrect_number':
                        addErrorToast({
                          message: intl.formatMessage({ id: 'formPublic.postSubmission.incorrectNumber' }),
                        });
                        break;
                      default:
                        addErrorToast({
                          message: intl.formatMessage({ id: 'formPublic.postSubmission.genericDecline' }),
                        });
                        break;
                    }
                    deleteSubmission(organizationId, form.id, submissionId, setupIntentId);
                  } else {
                    history.push(`/registrations/${form.id}/confirm/${submissionId}`);
                  }
                } else {
                  history.push(`/registrations/${form.id}/confirm/${submissionId}`);
                }
              } else {
                history.push(`/registrations/${form.id}/confirm/${submissionId}`);
              }
            })
            .catch((e) => {
              if (e.response.status === 400) {
                if (e.response.data && e.response.data.errors) {
                  if (_.find(e.response.data.errors, { error: 'noRecaptcha' })) {
                    addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.noRecaptcha' }) });
                  } else if (_.find(e.response.data.errors, { error: 'limit' })) {
                    addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.limitError' }) });
                  } else if (_.find(e.response.data.errors, { error: 'submissionLimit' })) {
                    addErrorToast({ message: intl.formatMessage({ id: 'errors.limitReached' }) });
                  } else {
                    addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.genericError' }) });
                  }
                }
              } else if (e.response.status === 402) {
                addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.genericDecline' }) });
              } else if (e.response.status === 403) {
                addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.invalidRecaptcha' }) });
              } else if (e.response.status === 410) {
                addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.incorrectDiscount' }) });
                setDiscount(null);
              }
            });
        }
      } else {
        if (!customerEmail) {
          addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.missingEmail' }) });
        } else if (!cardHolderName) {
          addErrorToast({ message: intl.formatMessage({ id: 'formPublic.postSubmission.missingName' }) });
        }
      }
    }
    setSubmitting(false);
  };

  if (noFields) {
    return <FormattedMessage id="formPublic.noFields"></FormattedMessage>;
  }

  if (limitReached) {
    return (
      <div className="d-flex justify-content-center">
        <Card className="flex-1 form" style={{ maxWidth: '1000px' }}>
          <Card.Body>
            <FormattedMessage id="errors.limitReached" />
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (disableForm) {
    return (
      <div>
        <FormattedMessage id="errors.genericRefresh" />
      </div>
    );
  }

  if (form.state !== FormStates.Active) {
    return (
      <div className="d-flex justify-content-center">
        <Card className="flex-1 form" style={{ maxWidth: '1000px' }}>
          <Card.Body>
            <FormattedMessage id="formPublic.postSubmission.inactiveFormError" />
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (steps.length == 0) return null;

  const renderRecaptcha = () => {
    if (form.recaptchaEnabled) {
      return (
        <div className="mb-3">
          <ReCAPTCHA
            sitekey={window.configs && window.configs.recaptcha ? window.configs.recaptcha : ''}
            onChange={(val) => {
              setRecaptchaRequired(false);
              setRecaptchaToken(val);
            }}
            hl={getLanguage()}
          />
          {recaptchaRequired && <Feedback touched={true} error={'Recaptcha is required'}></Feedback>}
        </div>
      );
    } else {
      return null;
    }
  };

  const renderTooltip = (text: string, id?: string | undefined) => (
    <Tooltip id={id ? id : 'copy-tooltip'}>{text}</Tooltip>
  );

  const handleCustomerEmail = (email: string) => {
    setCustomerEmail(email);
  };

  const handleCardHolderName = (name: string) => {
    setCardHolderName(name);
  };

  const handleOtherPayment = (isOtherPayment: boolean) => {
    setOtherPayment(isOtherPayment);
  };

  const handleDiscount = (discount: Discount) => {
    setDiscount(discount);
  };

  return (
    <div className="form-steps d-flex justify-content-center">
      <div className="flex-1 justify-content-center">
        {formikValues && formikSchema && stepsState && (
          <Formik
            validationSchema={formikSchema[steps[currentStep].id]}
            initialValues={formikValues}
            onSubmit={async (values, helpers) => {
              if (currentStep < steps.length - 1) {
                const nextStep = currentStep + 1;
                setCurrentStep(nextStep);
                helpers.setTouched(touchedSteps[steps[nextStep].id] || {});
                scrollToTop();
              } else {
                if (form.recaptchaEnabled) {
                  if (!recaptchaToken) {
                    setRecaptchaRequired(true);
                  } else {
                    onSubmit(values);
                  }
                } else {
                  onSubmit(values);
                }
              }
            }}
          >
            {({ values, touched, handleSubmit, setTouched, setFieldValue }) => {
              return (
                <div className="d-flex justify-content-center">
                  <Card className="flex-1 form" style={{ maxWidth: '1000px' }}>
                    <Card.Body>
                      <div className="d-flex flex-row align-items-sm-end align-items-center mb-4 flex-sm-row flex-row">
                        <Avatar height={150} width={150} src={form.avatar || organization.avatar} />
                        <div className="d-flex flex-column ms-sm-4 mb-2">
                          <div className="organization-name">{organization.name}</div>
                          <div className="form-name">{getText(form.name, intl.locale)}</div>
                        </div>
                        {showShareModal && (
                          <div className="share-btn-wrapper d-flex align-items-start justify-content-end flex-grow-1 pb-2">
                            <Button
                              className="share-btn d-flex align-items-center pe-2 py-0"
                              variant="primary"
                              onClick={() => setOpenModal(true)}
                              disabled={!formSecurity?.update}
                            >
                              <span className="mdi mdi-share pe-2"></span>
                              {intl.formatMessage({ id: 'formPublic.shareForm' })}
                            </Button>
                          </div>
                        )}
                      </div>
                      {steps.length > 1 && (
                        <div className="form-progress">
                          {steps.map((step, index) => (
                            <div
                              key={`form-step-${index}`}
                              className={`form-progress-step ${index == currentStep ? 'active' : ''}`}
                              style={{ left: `${(index / (steps.length - 1)) * 100}%` }}
                            >
                              <span>{index + 1}</span>
                            </div>
                          ))}
                          <div className="form-progress-bar">
                            <div
                              className="form-progress-fill"
                              style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                      <Form
                        noValidate
                        onSubmit={(event) => {
                          setTouchedSteps({ ...touchedSteps, [steps[currentStep].id]: touched });
                          handleSubmit(event);
                        }}
                      >
                        {steps[currentStep].type == 'form-step' && (
                          <div>
                            <RulesHandler
                              steps={stepsState}
                              setSteps={(newSteps) => {
                                setStepsState(newSteps);
                                setFormikSchema(buildSchema(newSteps, form.fieldProperties));
                              }}
                              rules={form.content.rules}
                            />
                            <FormStep
                              fieldProperties={form.fieldProperties}
                              readOnlyMode={readOnlyMode}
                              step={stepsState[currentStep]}
                              addSection={(sectionId: string) =>
                                addSection(
                                  values,
                                  sectionId,
                                  stepsState,
                                  form.content.rules,
                                  (newSteps) => {
                                    setStepsState(newSteps);
                                    setFormikSchema(buildSchema(newSteps, form.fieldProperties));
                                  },
                                  setFieldValue,
                                )
                              }
                              removeSection={(sectionId: string) =>
                                removeSection(
                                  values,
                                  sectionId,
                                  stepsState,
                                  form.content.rules,
                                  (newSteps) => {
                                    setStepsState(newSteps);
                                    setFormikSchema(buildSchema(newSteps, form.fieldProperties));
                                  },
                                  setFieldValue,
                                )
                              }
                            />
                          </div>
                        )}
                        <PaymentSection
                          form={form}
                          currentStep={steps[currentStep]}
                          organizationName={organization.name || ''}
                          customerEmail={(email: string) => handleCustomerEmail(email)}
                          cardHolderName={(name: string) => handleCardHolderName(name)}
                          isOtherPayment={(isOtherPayment: boolean) => handleOtherPayment(isOtherPayment)}
                          discount={discount}
                          onDiscountChange={(newDiscount: Discount) => handleDiscount(newDiscount)}
                          paymentMode={paymentMode}
                          onPaymentModeChange={(paymentMode: string) => setPaymentMode(paymentMode)}
                        ></PaymentSection>
                        {!readOnlyMode && currentStep >= steps.length - 1 && <div>{renderRecaptcha()}</div>}
                        <div className="d-flex justify-content-end">
                          {currentStep > 0 && (
                            <Button
                              variant="secondary"
                              className="me-2 action-button"
                              onClick={() => {
                                const previousStep = currentStep - 1;
                                setTouched(touchedSteps[steps[previousStep].id]);
                                setCurrentStep(previousStep);
                                scrollToTop();
                              }}
                            >
                              <span>{intl.formatMessage({ id: 'formPublic.previous' })}</span>
                            </Button>
                          )}
                          {!readOnlyMode && (
                            <Button
                              type="submit"
                              className="action-button"
                              disabled={submitting}
                              onClick={() => {
                                setTouched({});
                              }}
                            >
                              {currentStep < steps.length - 1 && (
                                <span>{intl.formatMessage({ id: 'formPublic.next' })}</span>
                              )}
                              {currentStep >= steps.length - 1 && (
                                <span>
                                  {submitting ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    intl.formatMessage({ id: 'formPublic.submit' })
                                  )}
                                </span>
                              )}
                            </Button>
                          )}
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>

                  <FormSummary className="d-none d-lg-block me-4" form={form} discount={discount}></FormSummary>
                  <ScrollToError />
                </div>
              );
            }}
          </Formik>
        )}
      </div>
      <ModalComponent
        closeSettings={() => setOpenModal(false)}
        buttons={[
          {
            text: intl.formatMessage({ id: 'formPublic.close' }),
            onClick: () => setOpenModal(false),
            buttonVariant: 'secondary',
          },
        ]}
        modalHeading={intl.formatMessage({
          id: 'formPublic.share',
        })}
        show={openModal}
      >
        <div className="d-flex flex-column">
          <div className="d-flex flex-row align-items-center justify-content-center pb-4">
            <OverlayTrigger
              delay={{ show: 50, hide: 100 }}
              overlay={renderTooltip(intl.formatMessage({ id: 'formPublic.link' }))}
              placement="top"
            >
              <Button
                variant="primary"
                className={classnames('link-btn me-1', { 'active-btn': showUrlLink })}
                onClick={() => {
                  setShowUrlLink(true);
                }}
              >
                <span className="modal-icons mdi mdi-link"></span>
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              delay={{ show: 50, hide: 100 }}
              overlay={renderTooltip(intl.formatMessage({ id: 'formPublic.code' }))}
              placement="top"
            >
              <Button
                variant="primary"
                className={classnames('code-btn me-1', { 'active-btn': !showUrlLink })}
                onClick={() => {
                  setShowUrlLink(false);
                }}
              >
                <span className="modal-icons mdi mdi-code-tags"></span>
              </Button>
            </OverlayTrigger>
          </div>
          <div className="d-flex flex-column">
            <div className="d-flex flex-row justify-content-between align-items-center p-3 pe-3">
              {showUrlLink && (
                <div className="w-50 d-flex justify-content-start align-self-center">
                  {intl.formatMessage({ id: 'formPublic.urlCopy' })}
                </div>
              )}
              {!showUrlLink && (
                <div className="w-50 d-flex justify-content-start align-self-center">
                  {intl.formatMessage({ id: 'formPublic.integrate' })}
                </div>
              )}
              <div className="d-flex justify-content-end align-items-center">
                {showUrlLink && (
                  <OverlayTrigger
                    delay={{ show: 50, hide: 100 }}
                    overlay={renderTooltip(intl.formatMessage({ id: 'formPublic.open' }))}
                    placement="top"
                    trigger={'hover'}
                  >
                    <a href={renderUrlLink()} target="_blank" rel="noreferrer">
                      <Button variant="ghost" className="open-btn me-1">
                        <span className="mdi mdi-open-in-new"></span>
                      </Button>
                    </a>
                  </OverlayTrigger>
                )}
                <OverlayTrigger
                  delay={{ show: 50, hide: 100 }}
                  overlay={renderTooltip(intl.formatMessage({ id: 'formPublic.copy' }))}
                  placement="top"
                  trigger={'hover'}
                >
                  <div className="copied-tooltip-wrapper">
                    <OverlayTrigger
                      delay={{ show: 50, hide: 100 }}
                      overlay={renderTooltip(intl.formatMessage({ id: 'formPublic.copied' }), 'copied-tooltip')}
                      placement="left"
                      defaultShow={false}
                      show={copied}
                    >
                      <Button
                        variant="ghost"
                        className="copy-btn me-1"
                        onClick={() => {
                          navigator.clipboard.writeText(modalTextValue).then(() => {
                            setCopied(true);
                            setTimeout(() => {
                              setCopied(false);
                            }, 2000);
                          });
                        }}
                      >
                        <span className="mdi mdi-content-copy"></span>
                      </Button>
                    </OverlayTrigger>
                  </div>
                </OverlayTrigger>
              </div>
            </div>
            <div className="p-3">
              <LongTextField
                id="urlOrHtmlTextField"
                enabled={true}
                value={showUrlLink ? renderUrlLink() : renderHtmlCode()}
                handleBlur={noop}
                handleChange={() =>
                  showUrlLink ? setModalTextValue(renderUrlLink) : setModalTextValue(renderHtmlCode())
                }
              ></LongTextField>
            </div>
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default FormSteps;
